import React from "react";
import { ExternalLink } from "lucide-react";
import { getAdFor } from "@/config/ads";

/**
 * <AdSlot placement="marketplace-inline" /> — small, honest-looking ad units.
 * Two variants:
 *   - "sidebar"  → thin vertical card (~180px tall)
 *   - "card"     → compact horizontal banner that slots into a grid without dominating it
 * Both wear an "AD" chip and muted styling so they read as sponsored placements,
 * not as featured content.
 */
export default function AdSlot({ placement, variant = "card", className = "" }) {
    const ad = getAdFor(placement);
    if (!ad) return null;

    if (variant === "sidebar") {
        return (
            <a
                href={ad.href}
                target="_blank"
                rel="noopener sponsored noreferrer"
                data-testid={`ad-${placement}`}
                data-ad-id={ad.id}
                className={`block rounded-lg overflow-hidden group ${className}`}
                style={{
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "linear-gradient(180deg, #0f0f0f 0%, #080808 100%)",
                    padding: "12px 12px 10px",
                }}
            >
                <div className="flex items-center justify-between mb-2">
                    <span
                        className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-sm"
                        style={{
                            background: "rgba(255,255,255,0.06)",
                            color: "rgba(255,255,255,0.55)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            letterSpacing: "0.22em",
                        }}
                    >
                        AD
                    </span>
                    <span className="text-[9px] uppercase tracking-widest text-onyx-500">
                        {ad.sponsor}
                    </span>
                </div>
                <div
                    className="text-sm text-white leading-snug"
                    style={{ fontWeight: 700 }}
                >
                    {ad.headline}
                </div>
                <p className="text-[11px] text-onyx-500 mt-1 leading-snug line-clamp-2">
                    {ad.subhead}
                </p>
                <div
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: ad.accent }}
                >
                    {ad.cta} <ExternalLink className="w-3 h-3" />
                </div>
            </a>
        );
    }

    // card = compact horizontal banner (shorter than listing cards, obviously "ad")
    return (
        <a
            href={ad.href}
            target="_blank"
            rel="noopener sponsored noreferrer"
            data-testid={`ad-${placement}`}
            data-ad-id={ad.id}
            className={`block rounded-lg overflow-hidden group relative ${className}`}
            style={{
                border: "1px solid rgba(255,255,255,0.08)",
                background: "linear-gradient(90deg, #0f0f0f 0%, #0a0a0a 100%)",
                minHeight: 96,
            }}
        >
            <span
                className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-sm z-10"
                style={{
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    letterSpacing: "0.22em",
                }}
            >
                AD
            </span>
            <div className="p-3 pr-10 flex items-center gap-3">
                <div
                    className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
                    style={{
                        background: `${ad.accent}18`,
                        border: `1px solid ${ad.accent}55`,
                        color: ad.accent,
                        fontSize: 18,
                    }}
                    aria-hidden
                >
                    {ad.emoji}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-widest text-onyx-500">
                        {ad.sponsor}
                    </div>
                    <div
                        className="text-sm text-white leading-snug truncate"
                        style={{ fontWeight: 700 }}
                    >
                        {ad.headline}
                    </div>
                    <p className="text-[11px] text-onyx-500 mt-0.5 leading-snug line-clamp-1">
                        {ad.subhead}
                    </p>
                </div>
                <div
                    className="shrink-0 hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold"
                    style={{
                        background: `${ad.accent}18`,
                        color: ad.accent,
                        border: `1px solid ${ad.accent}55`,
                    }}
                >
                    {ad.cta} <ExternalLink className="w-3 h-3" />
                </div>
            </div>
        </a>
    );
}
